import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Download, FileText, TrendingUp } from "lucide-react";

export default function Reports() {
  const [period, setPeriod] = useState("daily");
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/reports/summary?period=${period}`);
      setData(data);
    })();
  }, [period]);

  const money = (v) => "₹" + Number(v || 0).toFixed(2);

  const download = () => {
    if (!data) return;
    const rows = [
      ["day", "orders", "revenue", "pages"],
      ...data.by_day.map((d) => [d.day, d.orders, d.revenue, d.pages]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `maaprint-report-${period}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Analytics</p>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Reports</h1>
        </div>
        <Button variant="outline" onClick={download} data-testid="reports-download-btn">
          <Download size={16} className="mr-2" /> Download CSV
        </Button>
      </div>

      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="daily" data-testid="reports-tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="reports-tab-weekly">Weekly</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="reports-tab-monthly">Monthly</TabsTrigger>
        </TabsList>
        <TabsContent value={period} className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 border-border bg-card">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Orders</p>
              <p className="mt-2 text-3xl font-display font-semibold tabular-nums" data-testid="reports-total-orders">
                {data?.total_orders ?? 0}
              </p>
            </Card>
            <Card className="p-6 border-border bg-card">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Pages</p>
              <p className="mt-2 text-3xl font-display font-semibold tabular-nums" data-testid="reports-total-pages">
                {data?.total_pages ?? 0}
              </p>
            </Card>
            <Card className="p-6 border-border bg-card">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Revenue</p>
              <p className="mt-2 text-3xl font-display font-semibold tabular-nums text-brand-green" data-testid="reports-total-revenue">
                {money(data?.total_revenue)}
              </p>
            </Card>
            <Card className="p-6 border-border bg-card">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Color / BW</p>
              <p className="mt-2 text-3xl font-display font-semibold tabular-nums">
                {data?.color_orders ?? 0}<span className="text-muted-foreground text-lg"> / {data?.bw_orders ?? 0}</span>
              </p>
            </Card>
          </div>

          {/* Chart */}
          <Card className="p-6 border-border bg-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Trends</p>
                <h3 className="font-display text-xl font-semibold mt-1">Orders & Revenue</h3>
              </div>
              <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/20">
                <TrendingUp size={12} className="mr-1" /> {data?.by_day?.length ?? 0} days
              </Badge>
            </div>
            {data?.by_day?.length ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_day} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="orders" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="revenue" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="py-16 text-center text-muted-foreground text-sm">
                <FileText className="mx-auto text-muted-foreground/50" size={32} />
                <p className="mt-2">No data for this period yet.</p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
